import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../ui/button';
import { MessageCircle, QrCode, Download, Check, Loader2, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ShareActions({ imageBase64, patientPhone, patientName, generationType = '4k' }) {
  const [shareToken, setShareToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const getShareUrl = () => {
    if (!shareToken) return '';
    return `${API_URL}/api/share/${shareToken}`;
  };

  const createShareLink = async () => {
    if (shareToken) return shareToken;
    setLoading(true);
    try {
      const token = localStorage.getItem('neobreed_token') || sessionStorage.getItem('neobreed_token');
      const res = await fetch(`${API_URL}/api/share/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          image_base64: imageBase64,
          patient_name: patientName || '',
          generation_type: generationType
        })
      });
      const data = await res.json();
      setShareToken(data.token);
      return data.token;
    } catch (e) {
      console.error('Share link error:', e);
    } finally {
      setLoading(false);
    }
  };

  const normalizePhone = (raw) => {
    if (!raw) return '';
    let p = raw.replace(/[\s\-\(\)]/g, '').replace(/^\+/, '');
    if (p.startsWith('0')) p = '90' + p.slice(1);
    else if (!p.startsWith('90') && p.length === 10) p = '90' + p;
    return p;
  };

  const handleWhatsApp = async () => {
    const token = await createShareLink();
    if (!token) return;
    const url = `${API_URL}/api/share/${token}`;
    const phone = normalizePhone(patientPhone);
    const text = encodeURIComponent(`NeoBreed AI - ${generationType.toUpperCase()} sonucunuz hazir!\n\nGorselinizi indirmek icin:\n${url}`);
    const waUrl = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(waUrl, '_blank');
  };

  const handleQR = async () => {
    await createShareLink();
    setShowQR(true);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = imageBase64;
    link.download = `neobreed_${generationType}_${Date.now()}.png`;
    link.click();
  };

  return (
    <div className="space-y-3" data-testid="share-actions">
      <div className="flex gap-2 flex-wrap">
        <Button
          onClick={handleWhatsApp}
          disabled={loading}
          className="bg-[#25D366] hover:bg-[#1da851] text-white flex-1 min-w-[120px]"
          size="sm"
          data-testid="share-whatsapp"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-1.5" />}
          WhatsApp
        </Button>

        <Button
          onClick={handleQR}
          disabled={loading}
          variant="outline"
          className="flex-1 min-w-[120px] border-slate-300"
          size="sm"
          data-testid="share-qr"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <QrCode className="w-4 h-4 mr-1.5" />}
          QR Kod
        </Button>

        <Button
          onClick={handleDownload}
          variant="outline"
          className="flex-1 min-w-[120px] border-slate-300"
          size="sm"
          data-testid="share-download"
        >
          <Download className="w-4 h-4 mr-1.5" />
          Indir
        </Button>
      </div>

      {showQR && shareToken && (
        <div className="bg-white border-2 border-slate-200 rounded-xl p-4 text-center space-y-3 relative">
          <button onClick={() => setShowQR(false)} className="absolute top-2 right-2 text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
          <p className="text-xs text-slate-500 font-medium">Gorseli indirmek icin QR kodu tarayin</p>
          <div className="flex justify-center">
            <QRCodeSVG
              value={getShareUrl()}
              size={180}
              bgColor="#ffffff"
              fgColor="#1e293b"
              level="M"
              includeMargin={true}
            />
          </div>
          <p className="text-[10px] text-slate-400 break-all">{getShareUrl()}</p>
        </div>
      )}
    </div>
  );
}
